/**
 * Extract numeric threshold from a score description string.
 * Examples: "> 100" -> 100, "50–49" -> 49.5, "< 6" -> 6, "25–50%" -> 37.5
 */
export function extractThreshold(description: string): number | null {
    // Remove any text descriptions and percentages
    // Use \p{L} Unicode property to match letters from any language (including Portuguese accented chars)
    const cleaned = description.replace(/%/g, '').replace(/[\p{L}:]/gu, '').trim();

    // Handle ranges like "50–49", "1000–999", "25–50"
    const rangeMatch = cleaned.match(/(-?\d+(?:\.\d+)?)\s*[–-]\s*(-?\d+(?:\.\d+)?)/);
    if (rangeMatch) {
        const a = parseFloat(rangeMatch[1]);
        const b = parseFloat(rangeMatch[2]);
        return (a + b) / 2; // Return midpoint
    }

    // Handle "> X" or "< X"
    const comparisonMatch = cleaned.match(/[><]\s*(-?\d+(?:\.\d+)?)/);
    if (comparisonMatch) {
        return parseFloat(comparisonMatch[1]);
    }

    // Handle single numbers
    const singleMatch = cleaned.match(/(-?\d+(?:\.\d+)?)/);
    if (singleMatch) {
        return parseFloat(singleMatch[1]);
    }

    return null;
}

/**
 * Calculate raw value from score based on subcategory score ranges.
 * Uses linear interpolation (reverse of calculateScoreFromRawValue).
 */
export function calculateRawValueFromScore(
    score: number,
    scoreRanges: Record<string, string>
): number | null {
    // Parse thresholds from score ranges
    const thresholds: { score: number; value: number }[] = [];

    for (const [scoreStr, description] of Object.entries(scoreRanges)) {
        const s = parseInt(scoreStr);
        const value = extractThreshold(description);
        if (value !== null && !isNaN(s)) {
            thresholds.push({ score: s, value });
        }
    }

    if (thresholds.length < 2) return null;

    // Sort by score (0 to 5)
    thresholds.sort((a, b) => a.score - b.score);

    // Handle edge cases: score beyond the range
    if (score <= thresholds[0].score) return thresholds[0].value;
    if (score >= thresholds[thresholds.length - 1].score) return thresholds[thresholds.length - 1].value;

    // Find the two thresholds to interpolate between
    for (let i = 0; i < thresholds.length - 1; i++) {
        const lower = thresholds[i];
        const upper = thresholds[i + 1];

        if (score >= lower.score && score <= upper.score) {
            // Linear interpolation
            const scoreProgress = (score - lower.score) / (upper.score - lower.score);
            const interpolatedValue = lower.value + scoreProgress * (upper.value - lower.value);

            // Round to 2 decimal places
            return Math.round(interpolatedValue * 100) / 100;
        }
    }

    return null;
}

/**
 * Calculate score from raw value based on subcategory score ranges.
 * Uses linear interpolation between threshold points.
 */
export function calculateScoreFromRawValue(
    rawValue: number,
    scoreRanges: Record<string, string>
): number | null {
    // Parse thresholds from score ranges
    const thresholds: { score: number; value: number }[] = [];

    for (const [scoreStr, description] of Object.entries(scoreRanges)) {
        const score = parseInt(scoreStr);
        const value = extractThreshold(description);
        if (value !== null && !isNaN(score)) {
            thresholds.push({ score, value });
        }
    }

    if (thresholds.length < 2) return null;

    // Sort by score (0 to 5)
    thresholds.sort((a, b) => a.score - b.score);

    // Determine if inverse relationship (score 0 has higher value than score 5)
    const isInverse = thresholds[0].value > thresholds[thresholds.length - 1].value;

    // Sort thresholds by value for interpolation
    const sortedByValue = [...thresholds].sort((a, b) =>
        isInverse ? b.value - a.value : a.value - b.value
    );

    // Handle edge cases: value beyond the range
    if (isInverse) {
        // Lower values = higher scores
        if (rawValue >= sortedByValue[0].value) return sortedByValue[0].score;
        if (rawValue <= sortedByValue[sortedByValue.length - 1].value) return sortedByValue[sortedByValue.length - 1].score;
    } else {
        // Higher values = higher scores
        if (rawValue <= sortedByValue[0].value) return sortedByValue[0].score;
        if (rawValue >= sortedByValue[sortedByValue.length - 1].value) return sortedByValue[sortedByValue.length - 1].score;
    }

    // Find the two thresholds to interpolate between
    for (let i = 0; i < sortedByValue.length - 1; i++) {
        const lower = sortedByValue[i];
        const upper = sortedByValue[i + 1];

        const inRange = isInverse
            ? rawValue <= lower.value && rawValue >= upper.value
            : rawValue >= lower.value && rawValue <= upper.value;

        if (inRange) {
            // Linear interpolation
            const valueRange = Math.abs(upper.value - lower.value);
            const valueProgress = isInverse
                ? (lower.value - rawValue) / valueRange
                : (rawValue - lower.value) / valueRange;
            const scoreRange = upper.score - lower.score;
            const interpolatedScore = lower.score + (valueProgress * scoreRange);

            // Round to 1 decimal place
            return Math.round(interpolatedScore * 10) / 10;
        }
    }

    return null;
}

export const EMPTY_SCORE = { score: null, reliability: 1.0, raw_value: null };
